import { useEffect, useRef } from "react";
import { listarTarefas, Tarefa } from "../services/tarefas";
import { useAuthStore } from "../store/authStore";
import { useLembretesCalendarioStore } from "../store/lembretesCalendarioStore";

const KEY_ATIVO = "tarefas_lembrete_ativo";
const KEY_ANTECEDENCIA = "tarefas_lembrete_antecedencia_min";
const KEY_REPETICOES = "tarefas_lembrete_repeticoes";
const KEY_INTERVALO = "tarefas_lembrete_intervalo_min";

export function getTarefasLembreteAtivo(): boolean {
  try { return localStorage.getItem(KEY_ATIVO) === "1"; } catch { return false; }
}
export function setTarefasLembreteAtivo(v: boolean) {
  try { localStorage.setItem(KEY_ATIVO, v ? "1" : "0"); } catch {}
}
export function getTarefasAntecedencia(): number {
  try {
    const v = parseInt(localStorage.getItem(KEY_ANTECEDENCIA) || "60", 10);
    return Number.isFinite(v) && v > 0 ? v : 60;
  } catch { return 60; }
}
export function setTarefasAntecedencia(min: number) {
  try { localStorage.setItem(KEY_ANTECEDENCIA, String(Math.max(1, Math.floor(min)))); } catch {}
}
export function getTarefasRepeticoes(): number {
  try {
    const v = parseInt(localStorage.getItem(KEY_REPETICOES) || "1", 10);
    return Number.isFinite(v) && v > 0 ? v : 1;
  } catch { return 1; }
}
export function setTarefasRepeticoes(n: number) {
  try { localStorage.setItem(KEY_REPETICOES, String(Math.max(1, Math.min(10, Math.floor(n))))); } catch {}
}
export function getTarefasIntervalo(): number {
  try {
    const v = parseInt(localStorage.getItem(KEY_INTERVALO) || "30", 10);
    return Number.isFinite(v) && v > 0 ? v : 30;
  } catch { return 30; }
}
export function setTarefasIntervalo(min: number) {
  try { localStorage.setItem(KEY_INTERVALO, String(Math.max(5, Math.floor(min)))); } catch {}
}

export const TAREFA_ANTECEDENCIA_OPCOES: Array<{ valor: number; label: string }> = [
  { valor: 15, label: "15 minutos antes" },
  { valor: 30, label: "30 minutos antes" },
  { valor: 60, label: "1 hora antes" },
  { valor: 120, label: "2 horas antes" },
  { valor: 240, label: "4 horas antes" },
  { valor: 1440, label: "1 dia antes" },
];

export const TAREFA_INTERVALO_OPCOES: Array<{ valor: number; label: string }> = [
  { valor: 10, label: "a cada 10 minutos" },
  { valor: 30, label: "a cada 30 minutos" },
  { valor: 60, label: "a cada 1 hora" },
  { valor: 120, label: "a cada 2 horas" },
];

/**
 * Verifica tarefas atribuídas ao usuário com prazo próximo e dispara
 * notificação (browser + sininho). Repete N vezes em intervalos configuráveis.
 * Persiste contagem em localStorage por tarefa+data pra não duplicar entre reloads.
 */
export function useTarefasNotificacoes() {
  const userId = useAuthStore((s) => s.user?.id);
  const addLembrete = useLembretesCalendarioStore((s) => s.add);
  const ultimoTick = useRef(0);

  useEffect(() => {
    if (!userId) return;
    let cancelado = false;

    async function tick() {
      if (cancelado) return;
      if (!getTarefasLembreteAtivo()) return;

      const now = Date.now();
      if (now - ultimoTick.current < 25_000) return;
      ultimoTick.current = now;

      const antecedencia = getTarefasAntecedencia();
      const repeticoes = getTarefasRepeticoes();
      const intervalo = getTarefasIntervalo();

      let tarefas: Tarefa[];
      try {
        tarefas = await listarTarefas({ escopo: "minhas" });
      } catch { return; }
      if (cancelado) return;

      const podeNotificar = typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted";
      const agora = new Date();

      for (const t of tarefas) {
        if (t.status === "concluido") continue;
        const prazo = new Date(t.prazo);
        const minutosFaltando = (prazo.getTime() - agora.getTime()) / 60000;
        if (minutosFaltando < 0 || minutosFaltando > antecedencia) continue;

        const chaveBase = `tarefa-${t.id}-${prazo.toISOString().slice(0, 10)}`;
        const chaveContador = `tarefa_notif_count_${chaveBase}`;
        const chaveUltima = `tarefa_notif_last_${chaveBase}`;

        let count = 0;
        let ultima = 0;
        try {
          count = parseInt(localStorage.getItem(chaveContador) || "0", 10) || 0;
          ultima = parseInt(localStorage.getItem(chaveUltima) || "0", 10) || 0;
        } catch {}

        if (count >= repeticoes) continue;
        if (count > 0 && now - ultima < intervalo * 60_000) continue;

        const titulo = `⏰ Tarefa: ${t.titulo}`;
        const corpo = (() => {
          const restante = Math.max(0, Math.round(minutosFaltando));
          if (restante === 0) return "Vence agora";
          if (restante < 60) return `Vence em ${restante} min`;
          if (restante < 1440) return `Vence em ${Math.round(restante / 60)}h`;
          return `Vence em ${Math.round(restante / 1440)}d`;
        })();

        if (podeNotificar) {
          try { new Notification(titulo, { body: corpo, tag: chaveBase }); } catch {}
        }

        addLembrete({
          id: `${chaveBase}-${count + 1}`,
          titulo,
          mensagem: corpo + (t.projeto ? ` · ${t.projeto.nome}` : ""),
          tipo: "outro",
          emoji: "⏰",
          link: `/tarefas?tarefa=${t.id}`,
          criadoEm: new Date().toISOString(),
        });

        try {
          localStorage.setItem(chaveContador, String(count + 1));
          localStorage.setItem(chaveUltima, String(now));
        } catch {}
      }
    }

    tick();
    const id = setInterval(tick, 60_000);
    return () => { cancelado = true; clearInterval(id); };
  }, [userId, addLembrete]);
}
